import { Check, ChevronDown, Search } from 'lucide-react';
import {
  Children,
  isValidElement,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type OptionHTMLAttributes,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

type Props = {
  children: ReactNode;
  value?: string;
  defaultValue?: string;
  name?: string;
  className?: string;
  disabled?: boolean;
  required?: boolean;
  'aria-label'?: string;
  onValueChange?: (value: string) => void;
  onSearchChange?: (query: string) => void;
  loading?: boolean;
};

/** Searchable single-select with native FormData, validation and reset. */
export function SearchSelect({
  children,
  value,
  defaultValue,
  name,
  className = '',
  disabled,
  required,
  'aria-label': explicitLabel,
  onValueChange,
  onSearchChange,
  loading,
}: Props) {
  const options = Children.toArray(children).flatMap((child) => {
    if (!isValidElement<OptionHTMLAttributes<HTMLOptionElement>>(child) || child.type !== 'option')
      return [];
    const label = Children.toArray(child.props.children).join('');
    return [
      {
        value: String(child.props.value ?? label),
        label,
        disabled: child.props.disabled,
        hidden: child.props.hidden,
      },
    ];
  });
  const [internal, setInternal] = useState(defaultValue ?? options[0]?.value ?? '');
  const selected =
    value ??
    (options.some((option) => option.value === internal)
      ? internal
      : (defaultValue ?? options[0]?.value ?? ''));
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [invalid, setInvalid] = useState(false);
  const [label, setLabel] = useState(explicitLabel ?? 'Auswahl');
  const [position, setPosition] = useState({ left: 0, top: 0, width: 0, maxHeight: 300 });
  const trigger = useRef<HTMLButtonElement>(null);
  const native = useRef<HTMLSelectElement>(null);
  const popup = useRef<HTMLDivElement>(null);
  const search = useRef<HTMLInputElement>(null);
  const listId = useId();
  const visible = loading
    ? []
    : options.filter(
        (option) =>
          !option.disabled &&
          !option.hidden &&
          !(onSearchChange && query && !option.value) &&
          (onSearchChange ||
            option.label.toLocaleLowerCase('de').includes(query.toLocaleLowerCase('de'))),
      );
  const current = options.find((option) => option.value === selected);

  useLayoutEffect(() => {
    if (open && !loading) {
      setActive(
        query
          ? 0
          : Math.max(
              0,
              visible.findIndex((option) => option.value === selected),
            ),
      );
    }
  }, [open, query, loading, selected]);

  useLayoutEffect(() => {
    setLabel(
      explicitLabel ??
        trigger.current?.closest('label')?.querySelector('.label')?.textContent ??
        'Auswahl',
    );
  }, [explicitLabel]);
  useEffect(() => {
    const form = native.current?.form;
    const reset = () => {
      setInternal(defaultValue ?? options[0]?.value ?? '');
      setInvalid(false);
      setOpen(false);
    };
    form?.addEventListener('reset', reset);
    return () => form?.removeEventListener('reset', reset);
  }, [defaultValue, options[0]?.value]);
  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent) => {
      if (
        event.target instanceof Node &&
        !popup.current?.contains(event.target) &&
        !trigger.current?.contains(event.target)
      )
        setOpen(false);
    };
    const close = (event: Event) => {
      if (event.target instanceof Node && popup.current?.contains(event.target)) return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', outside);
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close, true);
    return () => {
      document.removeEventListener('pointerdown', outside);
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [open]);
  useLayoutEffect(() => {
    if (open) {
      search.current?.focus();
      popup.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: 'nearest' });
    }
  }, [open, active]);
  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  function show() {
    const rect = trigger.current?.getBoundingClientRect();
    if (!rect || disabled) return;
    const roomBelow = window.innerHeight - rect.bottom - 12;
    const height = Math.min(320, Math.max(roomBelow, rect.top - 12));
    const width = Math.min(Math.max(rect.width, 240), window.innerWidth - 24);
    setPosition({
      left: Math.max(12, Math.min(rect.left, window.innerWidth - width - 12)),
      top: roomBelow >= height ? rect.bottom + 6 : Math.max(12, rect.top - height - 6),
      width,
      maxHeight: height,
    });
    setQuery('');
    onSearchChange?.('');
    setActive(
      Math.max(
        0,
        options
          .filter((option) => !option.disabled)
          .findIndex((option) => option.value === selected),
      ),
    );
    setOpen(true);
  }
  function choose(next: string) {
    setInvalid(Boolean(required && !next));
    setInternal(next);
    onValueChange?.(next);
    setOpen(false);
    trigger.current?.focus();
  }
  function keyboard(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
      trigger.current?.focus();
    } else if (event.key === 'Tab') {
      setOpen(false);
      trigger.current?.focus();
    } else if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
      event.preventDefault();
      setActive((index) =>
        event.key === 'Home'
          ? 0
          : event.key === 'End'
            ? visible.length - 1
            : Math.max(
                0,
                Math.min(visible.length - 1, index + (event.key === 'ArrowDown' ? 1 : -1)),
              ),
      );
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (visible[active]) choose(visible[active].value);
    }
  }
  return (
    <span className={`select-container ${className}`}>
      <select
        ref={native}
        name={name}
        value={selected}
        disabled={disabled}
        required={required}
        tabIndex={-1}
        aria-hidden="true"
        className="select-native"
        onChange={(event) => choose(event.target.value)}
        onInvalid={(event) => {
          event.preventDefault();
          setInvalid(true);
          show();
        }}
      >
        {children}
      </select>
      <button
        ref={trigger}
        type="button"
        className="select-trigger"
        role="combobox"
        aria-label={label}
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-haspopup="listbox"
        aria-required={required}
        aria-invalid={invalid || undefined}
        disabled={disabled}
        onClick={() => (open ? setOpen(false) : show())}
        onKeyDown={(event) => {
          if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
            event.preventDefault();
            show();
          }
        }}
      >
        <span className="truncate">{current?.label ?? 'Bitte auswählen'}</span>
        <ChevronDown size={16} className="shrink-0 text-muted" />
      </button>
      {open &&
        createPortal(
          <div ref={popup} className="select-popup" style={position}>
            <div className="select-search">
              <Search size={16} />
              <input
                ref={search}
                value={query}
                maxLength={200}
                role="combobox"
                aria-label={`${label} durchsuchen`}
                aria-expanded="true"
                aria-controls={listId}
                aria-autocomplete="list"
                aria-activedescendant={visible[active] ? `${listId}-${active}` : undefined}
                placeholder="Suchen…"
                onChange={(event) => {
                  setQuery(event.target.value);
                  setActive(0);
                  onSearchChange?.(event.target.value);
                }}
                onKeyDown={keyboard}
              />
            </div>
            <div className="select-options" id={listId} role="listbox" aria-label={label}>
              {loading && (
                <div className="p-3 text-sm text-muted" role="status">
                  Auswahl wird geladen…
                </div>
              )}
              {!loading && !visible.length && (
                <div className="p-3 text-sm text-muted" role="status">
                  Keine passenden Einträge.
                </div>
              )}
              {visible.map((option, index) => (
                <div
                  key={option.value}
                  id={`${listId}-${index}`}
                  role="option"
                  aria-selected={option.value === selected}
                  data-active={index === active}
                  className="select-option"
                  onPointerMove={() => setActive(index)}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => choose(option.value)}
                >
                  <span>{option.label}</span>
                  {option.value === selected && <Check size={16} />}
                </div>
              ))}
            </div>
            <div className="select-hint">
              {invalid ? 'Bitte einen Eintrag auswählen. ' : ''}↑ ↓ Navigieren · Enter Auswählen ·
              Esc Schließen
            </div>
          </div>,
          trigger.current?.closest('dialog') ?? document.body,
        )}
    </span>
  );
}
